import { useQuery } from "@tanstack/react-query";
import { activeValveNo } from "@tbm/shared";
import { api } from "../../lib/api";
import { fmtNum, fmtRelative } from "../../lib/format";

export function LiveTile() {
  const q = useQuery({
    queryKey: ["lubrication", "latest"],
    queryFn: api.lubrication.latest,
    refetchInterval: 15_000,
  });

  if (q.isLoading || !q.data) {
    return (
      <section className="card px-6 py-5 text-[12px] text-muted">
        Loading lubrication status…
      </section>
    );
  }

  const { record, cutterheadChainageM, recordsTotal, latestRecordTs, lagSeconds } =
    q.data;

  if (!record) {
    return (
      <section className="card px-5 py-4 flex flex-wrap items-baseline gap-x-6 gap-y-1 text-[12px] text-muted">
        <span className="text-text">No bentonite data.</span>
        <Stat label="Records" value={`${recordsTotal}`} />
        <Stat label="Latest" value={latestRecordTs ? fmtRelative(latestRecordTs) : "—"} />
      </section>
    );
  }

  const port = activeValveNo(record);
  const fillPct =
    record.actVolM3 != null && record.desVolM3 != null && record.desVolM3 > 0
      ? (record.actVolM3 / record.desVolM3) * 100
      : null;

  return (
    <section className="card relative overflow-hidden">
      <div
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{ background: "linear-gradient(90deg, #16a34a 0%, #16a34a 50%, #c8102e 100%)" }}
      />
      <div className="px-6 pt-5 pb-5 flex items-stretch gap-8 flex-wrap">
        <PulseBadge lagSeconds={lagSeconds} />
        <Hero label="Geology code" value={record.geo != null ? `${record.geo}` : "—"} />
        <PortRing active={port} />
        <Divider />
        <Field
          label="Cumulative volume"
          value={record.actVolM3 != null ? `${fmtNum(record.actVolM3, 2)} m³` : "—"}
          sub={
            fillPct != null
              ? `${fmtNum(fillPct, 1)}% of target ${fmtNum(record.desVolM3, 2)} m³`
              : record.desVolM3 != null
                ? `target ${fmtNum(record.desVolM3, 2)} m³`
                : undefined
          }
        />
        <Field
          label="Latest event"
          value={
            record.igvL != null || record.agvL != null
              ? `${fmtNum((record.igvL ?? 0) + (record.agvL ?? 0), 1)} L`
              : "—"
          }
          sub={
            record.igvL != null && record.agvL != null
              ? `init ${fmtNum(record.igvL, 1)} + add'l ${fmtNum(record.agvL, 1)}`
              : undefined
          }
        />
        <Divider />
        <Field
          label="Skin friction"
          value={record.skfKnM != null ? `${fmtNum(record.skfKnM, 1)} kN/m` : "—"}
        />
        <Field
          label="Cutterhead chainage"
          value={cutterheadChainageM != null ? `${fmtNum(cutterheadChainageM, 1)} m` : "—"}
        />
        <div className="ml-auto flex flex-col items-end justify-end leading-tight">
          <div className="text-[10px] uppercase tracking-[0.2em] text-dim">Updated</div>
          <div className="mono-nums text-[12px] text-text">{fmtRelative(record.ts)}</div>
        </div>
      </div>
    </section>
  );
}

/** Anything older than this stops counting as live. */
const LIVE_MAX_LAG_S = 15 * 60;

function PulseBadge({ lagSeconds }: { lagSeconds: number | null }) {
  // The badge used to be hard-coded green + pulsing regardless of the
  // record's age, so a bentonite record from weeks ago still announced
  // itself as LIVE.
  const live = lagSeconds != null && lagSeconds <= LIVE_MAX_LAG_S;
  return (
    <div className="flex flex-col items-center justify-center gap-1.5 min-w-[64px]">
      <div className="relative w-3.5 h-3.5">
        <span
          className={`absolute inset-0 rounded-full ${live ? "bg-green-500" : "bg-dim"}`}
        />
        {live && (
          <span className="absolute inset-0 rounded-full bg-green-500 opacity-60 animate-ping" />
        )}
      </div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-dim">
        {live ? "Live" : "Stale"}
      </div>
    </div>
  );
}

function Hero({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col leading-tight">
      <div className="text-[10px] uppercase tracking-[0.2em] text-dim">{label}</div>
      <div className="mono-nums text-[34px] font-semibold text-text leading-none mt-1">{value}</div>
    </div>
  );
}

function Field({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="flex flex-col leading-tight justify-end">
      <div className="text-[10px] uppercase tracking-[0.2em] text-dim">{label}</div>
      <div className="mono-nums text-[18px] font-semibold text-text leading-none mt-1.5">
        {value}
      </div>
      {sub && <div className="text-[10.5px] text-muted mt-1">{sub}</div>}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-dim uppercase tracking-wider">{label}:</span>{" "}
      <span className="mono-nums text-text">{value}</span>
    </div>
  );
}

function Divider() {
  return <div className="w-px self-stretch bg-border1" />;
}

function PortRing({ active }: { active: 1 | 2 | 3 | null }) {
  const positions: Array<{ port: 1 | 2 | 3; cx: number; cy: number }> = [
    { port: 1, cx: 40, cy: 12 },
    { port: 2, cx: 64, cy: 56 },
    { port: 3, cx: 16, cy: 56 },
  ];
  return (
    <div className="flex flex-col leading-tight">
      <div className="text-[10px] uppercase tracking-[0.2em] text-dim mb-1">Active port</div>
      <div className="flex items-center gap-3">
        <svg width={70} height={70} viewBox="0 0 80 80" className="block">
          <circle cx={40} cy={40} r={28} fill="none" stroke="#e5e7eb" strokeWidth={1.5} />
          {positions.map((p) => {
            const isActive = active === p.port;
            return (
              <g key={p.port}>
                {isActive && (
                  <circle cx={p.cx} cy={p.cy} r={11} fill="#16a34a" opacity={0.18}>
                    <animate
                      attributeName="r"
                      values="9;13;9"
                      dur="1.6s"
                      repeatCount="indefinite"
                    />
                  </circle>
                )}
                <circle
                  cx={p.cx}
                  cy={p.cy}
                  r={9}
                  fill={isActive ? "#16a34a" : "#e5e7eb"}
                  stroke={isActive ? "#15803d" : "#d4d4d4"}
                  strokeWidth={1}
                />
                <text
                  x={p.cx}
                  y={p.cy + 3}
                  textAnchor="middle"
                  fontSize={9}
                  fontWeight={600}
                  fill={isActive ? "#fff" : "#6b7280"}
                  fontFamily="Helvetica, Arial, sans-serif"
                >
                  {p.port}
                </text>
              </g>
            );
          })}
        </svg>
        <div className="mono-nums text-[18px] font-semibold text-text leading-none">
          {active != null ? `Port ${active}` : "—"}
        </div>
      </div>
    </div>
  );
}
